
import React, { useState, useEffect } from "react";
import { useLeads, Lead } from "@/context/LeadContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  MessageSquare,
  Send,
  Phone,
  BarChart2,
  Users,
  RefreshCw,
  Sparkles,
  FileText,
  Zap
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import SMSTracker from "@/components/messages/SMSTracker";
import UnassignedLeadNotifier from "@/components/messages/UnassignedLeadNotifier";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MessagesContent = () => {
  const { leads, sendSMS, refreshLeads } = useLeads();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("unassigned");
  const [refreshing, setRefreshing] = useState(false);
  const [templates] = useState<string[]>([
    "Καλησπέρα σας! Το αίτημά σας έχει ληφθεί και θα επικοινωνήσουμε σύντομα μαζί σας.",
    "Σας ευχαριστούμε για το ενδιαφέρον σας. Ένας εκπρόσωπός μας θα επικοινωνήσει μαζί σας εντός 24 ωρών.",
    "Σας ενημερώνουμε ότι το αίτημά σας προωθήθηκε στο τμήμα εξυπηρέτησης πελατών."
  ]);

  const selectedLead = leads.find(lead => lead.id === selectedLeadId);

  const handleRefreshData = async () => {
    try {
      setRefreshing(true);
      await refreshLeads();
      toast({
        title: "Επιτυχής ανανέωση",
        description: "Τα δεδομένα ενημερώθηκαν επιτυχώς",
      });
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast({
        title: "Σφάλμα ανανέωσης",
        description: "Υπήρξε πρόβλημα κατά την ανανέωση των δεδομένων",
        variant: "destructive"
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSend = async () => {
    if (!selectedLead || !message.trim()) {
      toast({
        title: "Σφάλμα",
        description: "Παρακαλώ επιλέξτε πελάτη και συμπληρώστε το μήνυμα",
        variant: "destructive"
      });
      return;
    }

    try {
      setSending(true);
      await sendSMS(selectedLead.id, message, selectedLead.phone);

      toast({
        title: "Επιτυχία",
        description: "Το μήνυμα στάλθηκε επιτυχώς",
      });

      setMessage("");
    } catch (error) {
      console.error("Error sending SMS:", error);
      toast({
        title: "Σφάλμα αποστολής",
        description: "Υπήρξε πρόβλημα κατά την αποστολή του μηνύματος",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleNotificationSuccess = () => {
    refreshLeads();
  };

  const useTemplate = (template: string) => {
    let personalizedMessage = template;
    if (selectedLead?.name) {
      personalizedMessage = personalizedMessage.replace(
        "Καλησπέρα σας!",
        `Καλησπέρα σας ${selectedLead.name}!`
      );
    }
    setMessage(personalizedMessage);
  };

  const validLeads = leads.filter(lead => lead.phone && lead.phone.trim() !== "");
  const unassignedLeadsCount = leads.filter(lead => !lead.partnerId).length;
  const smsSentCount = leads.filter(lead => lead.smsStatus === "sent" || lead.smsStatus === "delivered").length;

  const goToStatistics = () => {
    navigate("/admin/messages/statistics");
  };

  useEffect(() => {
    refreshLeads();
  }, []);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg">
            <MessageSquare className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Μηνύματα</h1>
            <p className="text-slate-500 mt-0.5">Αποστολή SMS και ειδοποιήσεις</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleRefreshData}
            variant="outline"
            disabled={refreshing}
            className="border-slate-200"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Ανανέωση
          </Button>
          <Button onClick={goToStatistics} variant="outline" className="border-slate-200">
            <BarChart2 className="h-4 w-4 mr-2" />
            Στατιστικά
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-violet-50 to-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-violet-600">{unassignedLeadsCount}</p>
                <p className="text-sm text-slate-600 font-medium">Μη Ανατεθειμένα</p>
              </div>
              <div className="p-2 rounded-lg bg-violet-100">
                <Users className="h-5 w-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{smsSentCount}</p>
                <p className="text-sm text-slate-600 font-medium">SMS Απεσταλμένα</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100">
                <Send className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-600">{validLeads.length}</p>
                <p className="text-sm text-slate-600 font-medium">Διαθέσιμες Επαφές</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100">
                <Phone className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="unassigned" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Users className="h-4 w-4 mr-2" />
            Μη Ανατεθειμένοι
            {unassignedLeadsCount > 0 && (
              <Badge variant="secondary" className="ml-2 bg-violet-100 text-violet-700">
                {unassignedLeadsCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="compose" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <MessageSquare className="h-4 w-4 mr-2" />
            Αποστολή SMS
          </TabsTrigger>
        </TabsList>

        <TabsContent value="unassigned" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <UnassignedLeadNotifier
                leads={leads}
                onSuccess={handleNotificationSuccess}
              />
            </div>

            <div>
              <SMSTracker leads={leads} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="compose" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              {/* Compose Card */}
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-violet-50">
                      <Send className="h-4 w-4 text-violet-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Σύνθεση Μηνύματος</CardTitle>
                      <CardDescription>Επιλέξτε παραλήπτη και γράψτε το μήνυμά σας</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Παραλήπτης</label>
                    <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                      <SelectTrigger className="bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Επιλέξτε παραλήπτη..." />
                      </SelectTrigger>
                      <SelectContent>
                        {validLeads.map((lead) => (
                          <SelectItem key={lead.id} value={lead.id}>
                            {lead.name} ({lead.phone})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedLead && (
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white font-semibold">{selectedLead.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{selectedLead.name}</p>
                          <p className="text-sm text-slate-500">{selectedLead.phone}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Μήνυμα</label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Γράψτε το μήνυμά σας..."
                      rows={5}
                      className="bg-slate-50 border-slate-200 resize-none"
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Χρησιμοποιήστε τα πρότυπα για γρήγορη σύνθεση</span>
                      <span className={message.length > 160 ? "text-amber-600" : ""}>
                        {message.length}/160 χαρακτήρες
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleSend}
                    disabled={sending || !selectedLeadId || !message.trim()}
                    className="w-full btn-gradient h-11"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {sending ? "Αποστολή..." : "Αποστολή SMS"}
                  </Button>
                </CardContent>
              </Card>

              {/* Templates Card */}
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-amber-50">
                      <FileText className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Πρότυπα Μηνυμάτων</CardTitle>
                      <CardDescription>Επιλέξτε ένα έτοιμο πρότυπο</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {templates.map((template, index) => (
                      <div
                        key={index}
                        className="p-4 border border-slate-200 rounded-xl hover:border-violet-300 hover:bg-violet-50/50 cursor-pointer transition-all group"
                        onClick={() => useTemplate(template)}
                      >
                        <p className="text-sm text-slate-600 group-hover:text-slate-900">{template}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-violet-600 hover:text-violet-700 hover:bg-violet-100 p-0 h-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            useTemplate(template);
                          }}
                        >
                          <Sparkles className="h-3 w-3 mr-1" />
                          Χρήση προτύπου
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <SMSTracker leads={leads} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Messages = () => (
  <MessagesContent />
);

export default Messages;
